/**
 * mcp-pending-writes-card.tsx — "Yêu cầu chờ duyệt": các thao tác GHI do AI (Claude qua MCP) xin làm, chưa thực hiện.
 * Duyệt = máy chủ chạy tool THẬT dưới danh tính người yêu cầu (kiểm lại quyền lúc duyệt); Từ chối = bỏ, không làm gì.
 * Chỉ hiện cho người có `ai_connector.approve` (backend cũng tự kiểm).
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Check, ShieldAlert, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loading, EmptyState, ErrorState } from '@/components/shared/feedback'
import {
  useApproveMcpWrite, useMcpPendingWrites, useRejectMcpWrite, type McpPendingStatus, type McpPendingWrite,
} from '@/hooks/use-mcp-connector'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'

type Tab = 'pending' | 'done'

const STATUS_VARIANT: Record<string, 'success' | 'secondary' | 'warning' | 'destructive'> = {
  confirmed: 'success', rejected: 'secondary', expired: 'secondary', failed: 'destructive', confirming: 'warning', pending: 'warning',
}

export function McpPendingWritesCard({ pendingCount }: { pendingCount: number }) {
  const { t } = useTranslation('settings')
  const [tab, setTab] = useState<Tab>('pending')
  // "Đã xử lý" = mọi trạng thái trừ đang chờ → lấy tất cả rồi lọc phía giao diện.
  const { data, isLoading, error, refetch } = useMcpPendingWrites(tab === 'pending' ? 'pending' : '')
  const approve = useApproveMcpWrite()
  const reject = useRejectMcpWrite()
  const busy = approve.isPending || reject.isPending

  const rows = (data ?? []).filter((r) => (tab === 'pending' ? r.status === 'pending' : r.status !== 'pending'))

  const onApprove = async (r: McpPendingWrite) => {
    const question = r.risk === 'high'
      ? t('settings:aiConnector.pending.approveConfirmHigh', { summary: r.summary ?? r.title })
      : t('settings:aiConnector.pending.approveConfirm', { summary: r.summary ?? r.title })
    // Rủi ro cao: hộp xác nhận kèm NGUYÊN VĂN tham số (summary bị cắt ngắn — vd tin sắp gửi khách phải đọc hết trước khi Duyệt).
    const full = r.risk === 'high' ? `\n\n${JSON.stringify(r.args, null, 2)}` : ''
    if (!window.confirm(question + full)) return
    try {
      await approve.mutateAsync(r.id)
      toast.success(t('settings:aiConnector.pending.approved'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const onReject = async (r: McpPendingWrite) => {
    if (!window.confirm(t('settings:aiConnector.pending.rejectConfirm', { summary: r.summary ?? r.title }))) return
    try {
      await reject.mutateAsync(r.id)
      toast.success(t('settings:aiConnector.pending.rejected'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <ShieldAlert className="h-4 w-4 text-primary" /> {t('settings:aiConnector.pending.title')}
          {pendingCount > 0 && <Badge variant="warning">{pendingCount}</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t('settings:aiConnector.pending.description')}</p>
        <div className="flex gap-1.5 pt-1">
          {(['pending', 'done'] as const).map((k) => (
            <Button key={k} size="sm" variant={tab === k ? 'default' : 'outline'} onClick={() => setTab(k)}>
              {t(`settings:aiConnector.pending.tab.${k}`)}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loading label={t('settings:aiConnector.pending.loading')} />
        ) : error ? (
          <ErrorState message={apiError(error)} onRetry={() => refetch()} />
        ) : !rows.length ? (
          <EmptyState
            icon={ShieldAlert}
            title={tab === 'pending' ? t('settings:aiConnector.pending.emptyPending') : t('settings:aiConnector.pending.emptyDone')}
            description={tab === 'pending' ? t('settings:aiConnector.pending.emptyHint') : undefined}
          />
        ) : (
          <ul className="divide-y rounded-lg border">
            {rows.map((r) => (
              <PendingRow key={r.id} r={r} tab={tab} busy={busy} onApprove={onApprove} onReject={onReject} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function PendingRow({ r, tab, busy, onApprove, onReject }: {
  r: McpPendingWrite; tab: Tab; busy: boolean
  onApprove: (r: McpPendingWrite) => void; onReject: (r: McpPendingWrite) => void
}) {
  const { t } = useTranslation('settings')
  const who = r.requestedBy?.fullName ?? r.clientName ?? '—'
  return (
    <li className="space-y-1.5 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
        <span className="min-w-0 break-words">{r.title}</span>
        {r.risk === 'high' && <Badge variant="destructive">{t('settings:aiConnector.pending.highRisk')}</Badge>}
        {tab === 'done' && <Badge variant={STATUS_VARIANT[r.status] ?? 'secondary'}>{t(`settings:aiConnector.pending.status.${r.status}`)}</Badge>}
      </div>
      {r.summary && <p className="break-words text-sm text-muted-foreground">{r.summary}</p>}
      {/* summary cắt ngắn từng tham số — người duyệt PHẢI thấy nguyên văn tham số trước khi bấm Duyệt (vd nội dung tin sắp gửi khách). */}
      <details className="text-xs" open={r.risk === 'high'}>
        <summary className="cursor-pointer text-muted-foreground">{t('settings:aiConnector.pending.argsToggle')}</summary>
        <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2">{JSON.stringify(r.args, null, 2)}</pre>
      </details>
      <p className="text-xs text-muted-foreground">
        {t('settings:aiConnector.pending.requestedBy', { who })}
        {r.clientName && r.requestedBy ? ` · ${r.clientName}` : ''} · {new Date(r.createdAt).toLocaleString(currentLocale())}
        {tab === 'pending' && r.expiresAt ? ` · ${t('settings:aiConnector.pending.expiresAt', { date: new Date(r.expiresAt).toLocaleString(currentLocale()) })}` : ''}
      </p>
      {tab === 'done' && r.result?.text && (
        <p className={`break-words rounded bg-muted px-2 py-1 text-xs ${r.result.ok ? '' : 'text-destructive'}`}>{r.result.text}</p>
      )}
      {tab === 'pending' && (
        <div className="flex flex-wrap gap-2 pt-0.5">
          <Button size="sm" disabled={busy} onClick={() => onApprove(r)}>
            <Check className="h-3.5 w-3.5" /> {t('settings:aiConnector.pending.approve')}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onReject(r)}>
            <X className="h-3.5 w-3.5" /> {t('settings:aiConnector.pending.reject')}
          </Button>
        </div>
      )}
    </li>
  )
}

export type { McpPendingStatus }
